import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTopInfoBarController } from 'src/hooks/useTopInfoBarController';
import InactiveNetworkLayout from './Layouts/InactiveNetwork';
import { DismissButton, TopInfoBarContainer, topInfoBarTransitionTime } from './TopInfoBar.styles';
import PeopleMagicProfitAnnouncementBar from './Layouts/PeopleMagicProfitAnnouncementBar';
import UnlockFreeMasterclass from './Layouts/UnlockFreeMasterclass';
import MyFreeGiftToYou from './Layouts/MyFreeGiftToYou';
import OptInGetFreeMasterclass from './Layouts/OptInGetFreeMasterclass';
import NovemberOfferAnnouncement from './Layouts/NovemberOfferAnnouncement';

export const TopBarInfoLayoutList = {
  inactiveNetworks: InactiveNetworkLayout,
  peopleMagicProfitAnnouncement: PeopleMagicProfitAnnouncementBar,
  unlockFreeMasterclass: UnlockFreeMasterclass,
  myFreeGiftToYou: MyFreeGiftToYou,
  optInGetFreeMasterclass: OptInGetFreeMasterclass,
  novemberOfferAnnouncement: NovemberOfferAnnouncement,
};

export type TopBarInfoLayouts = keyof typeof TopBarInfoLayoutList;

// This component in intended to be used and modified to add information in the top of the page when necessary.
// Currently, the component is relatively coupled to the Header at src/molecules/Shared/2023_Q4/Header/Header.tsx to display correctly on top
// In order to display it outside the Header or in a different header, further changes may be needed.
// Make sure to not create new components, just update this one in order to make it more customizable
const TopInfoBar = (): JSX.Element => {
  const [height, setHeight] = useState<number>();
  const [removeContent, setRemoveContent] = useState(false);
  const topBarRef = useRef<HTMLDivElement>();

  const { isVisible, content, hide, dismissible, backgroundColor, dismissButtonColor, customStyles } =
    useTopInfoBarController();
  const InfoContent = TopBarInfoLayoutList[content as TopBarInfoLayouts];

  const isHidden = useMemo(() => !isVisible || !InfoContent, [isVisible, InfoContent]);

  const setBarHeight = useCallback(() => {
    if (topBarRef.current) {
      setHeight(topBarRef.current.getBoundingClientRect().height);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener('resize', setBarHeight);

    return () => {
      window.removeEventListener('resize', setBarHeight);
    };
  }, []);

  useEffect(() => {
    setBarHeight();
    let timeout: NodeJS.Timeout;

    if (!isVisible && content) {
      timeout = setTimeout(() => setRemoveContent(true), topInfoBarTransitionTime);
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isHidden, setBarHeight, content]);

  return (
    <TopInfoBarContainer
      ref={topBarRef}
      hasContent={!!content && !removeContent}
      barHeight={height}
      isHidden={isHidden}
      dismissible={dismissible}
      backgroundColor={backgroundColor}
      customStyles={customStyles}
    >
      {InfoContent && <InfoContent />}
      {dismissible && (
        <DismissButton
          onClick={() => hide()}
          buttonColor={dismissButtonColor}
        >
          X
        </DismissButton>
      )}
    </TopInfoBarContainer>
  );
};

export default TopInfoBar;
